// A transient notice for an action the server is carrying out: a creative
// pause that was accepted, a campaign pause that can be undone. One shape,
// so a screen reader hears it (`role="status" aria-live="polite"`) and every
// toast looks and behaves the same — the campaign editor's pause toast was a
// fixed-position div with inline styles and no live region until 2026-09-18.
//
// The caller owns the model and replaces it to show the next notice; a new
// `key` restarts the auto-dismiss clock, the same key leaves it running.

import { useEffect, useRef, type ReactNode } from 'react';

export interface ActionToastModel {
  key: number | string;
  message: ReactNode;
  action?: { label: string; onClick: () => void };
}

export function ActionToast({
  toast,
  onDismiss,
  ttlMs = 7000,
}: {
  toast: ActionToastModel | null;
  onDismiss: (key: ActionToastModel['key']) => void;
  ttlMs?: number;
}) {
  // The dismiss callback is read through a ref so an inline arrow at the call
  // site does not restart the timer on every render.
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const key = toast?.key;
  useEffect(() => {
    if (key === undefined) return;
    const timer = setTimeout(() => dismiss.current(key), ttlMs);
    return () => clearTimeout(timer);
  }, [key, ttlMs]);
  if (!toast) return null;
  return (
    <div className="action-toast" role="status" aria-live="polite" data-toast-key={String(toast.key)}>
      <span className="action-toast-message">{toast.message}</span>
      {toast.action && (
        <button type="button" className="action-toast-button" onClick={toast.action.onClick}>
          {toast.action.label}
        </button>
      )}
    </div>
  );
}
